import { parseISO } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Icon, Row } from '~/commonComponents';
import Button from '~/commonComponents/Button';
import { ChargeTimelineDaysPopup } from '~/components/ChargeTimelineDaysPopup';
import { SectionCard, ShiftRequestHeader } from '~/components/ShiftRequestRedesign/common';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { UserProfession } from '~/enums/UserProfession';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { Colors } from '~/themes/colors';
import { useIsFormValid } from '../hooks/useIsFormValid';
import { SelectedDatesCard } from './SelectedDatesCard';
import { ShiftDetailsForm, useShiftDetailsForm } from './ShiftDetailsForm';
import { SpeedUpCoverageSection } from './SpeedUpCoverage';
import { useSafeShiftDetailsLocationState } from './useSafeLocationState';

export const ShiftDetailsScreen = () => {
  const { control, watch, setValue, openField, toggleField } = useShiftDetailsForm();
  const isFormValid = useIsFormValid();
  const navigate = useNavigate();
  const { preselectedDate, professionType: navProfessionType } = useSafeShiftDetailsLocationState();

  const dates = watch('dates');

  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: true,
    onNavigationAttempt,
  });

  const handleRemoveDate = useCallback(
    (dateToRemove: Date) => {
      setValue(
        'dates',
        dates.filter((d) => d.getTime() !== dateToRemove.getTime()),
      );
    },
    [dates, setValue],
  );

  const handleAddDates = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/shift-request/select-dates');
  }, [allowNavigation, navigate]);

  const handleBack = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/shift-request/select-dates');
  }, [allowNavigation, navigate]);

  const handleReviewShifts = useCallback(() => {
    allowNavigation();
    navigate('/dashboard/multi-day/confirm');
  }, [allowNavigation, navigate]);

  useEffect(() => {
    if (preselectedDate) {
      const parsed = parseISO(preselectedDate);
      if (!isNaN(parsed.getTime())) {
        setValue('dates', [parsed]);
      }
    }
    if (navProfessionType) {
      const displayName = UserProfession.getName(navProfessionType) || navProfessionType;
      setValue('professionType', displayName);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Run once on mount: seed form from navigation state

  useEffect(() => {
    if (dates.length === 0 && !preselectedDate) {
      allowNavigation();
      navigate('/dashboard/shift-request/select-dates', { replace: true });
    }
  }, [dates.length, allowNavigation, navigate, preselectedDate]);

  return (
    <ShiftRequestHeader currentStep={1} onBack={handleBack} maxWidth={1120}>
      <Row marginTop={32} gap={24} flexWrap="wrap">
        <Column flex={1} minWidth={430}>
          <SectionCard title="Shift Details" testId="shift-details-card">
            <ShiftDetailsForm
              control={control}
              watch={watch}
              setValue={setValue}
              openField={openField}
              toggleField={toggleField}
            />
          </SectionCard>
        </Column>

        <Column flex={1} minWidth={430}>
          <SelectedDatesCard
            dates={dates}
            onRemoveDate={handleRemoveDate}
            onAddDates={handleAddDates}
          />
          <Box marginTop={24}>
            <SectionCard title="Speed Up Your Coverage" testId="speed-up-card">
              <SpeedUpCoverageSection control={control} watch={watch} />
            </SectionCard>
          </Box>
        </Column>
      </Row>

      <Button
        text="Review Shifts"
        data-testid="review-shifts-button"
        onClick={handleReviewShifts}
        disabled={!isFormValid}
        style={{
          width: '100%',
          maxWidth: 296,
          margin: '40px auto 0',
          display: 'block',
          backgroundColor: isFormValid ? Colors.primary_500 : Colors.primary_200,
        }}
      />

      {showNavigationGuard && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          Icon={() => (
            <div style={{ marginBottom: 20 }}>
              <Icon name="warning-outline" size={80} />
            </div>
          )}
          title="Are You Sure You Want to Leave?"
          description="Your shifts haven't been posted yet. Leaving now means the progress made to your request won't be saved."
          leftButtonText="Go Back"
          leftButtonAction={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          rightButtonText="Leave Anyway"
          rightButtonAction={navigateToPending}
          closePopup={() => {
            clearPendingNavigation();
            setShowNavigationGuard(false);
          }}
          dataTestId="navigation-guard-popup"
        />
      )}

      <ChargeTimelineDaysPopup screenName="ShiftDetails" />
    </ShiftRequestHeader>
  );
};
